import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import LegalDocumentModal from './LegalDocumentModal';
import { useAppSettings } from '../../hooks/useGlobalSettings';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

interface LocationState {
  email?: string;
  message?: string;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState<boolean>(false);

  const { signIn, signInWithGoogle } = useUser();
  const { isDarkMode } = useCustomTheme();
  const { appName, appDescription, platformTagline, appSubtitle } = useAppSettings();
  useDocumentTitle('Login');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Handle pre-populated data from registration redirect
  useEffect(() => {
    if (state) {
      if (state.email) {
        setEmail(state.email);
      }
      if (state.message) {
        setError(state.message);
      }
    }
  }, [state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await signIn(email, password);

      // The PublicRoute will automatically redirect users without tenantId to auto-provision
      // For now, we'll navigate to dashboard and let the routing logic handle it
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 2
      }}
    >
      <Grid container maxWidth="lg" spacing={4}>
        {/* Left side - Logo and branding */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center'
            }}
          >
            <Box
              component="img"
              src={isDarkMode ? "/Merit4all logo white.png" : "/Merit4all logo.png"}
              alt="Merit4all Logo"
              sx={{
                width: 240,
                height: 'auto',
                mb: 3,
                maxWidth: '100%'
              }}
            />
            <Typography variant="h3" component="h1" gutterBottom>
              {appName}
            </Typography>
            <Typography variant="h5" color="primary" sx={{ mb: 1, fontWeight: 500 }}>
              {appSubtitle}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {platformTagline}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
              {appDescription}
            </Typography>
          </Box>
        </Grid>

        {/* Right side - Login form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 400,
              mx: 'auto',
              borderRadius: 2
            }}
          >
            <Typography variant="h4" component="h2" gutterBottom align="center">
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Sign in to your account
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    Forgot your password?
                  </Typography>
                </Link>
              </Box>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{ py: 1.5 }}
              >
                Sign in with Google
              </Button>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{' '}
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Typography component="span" variant="body2" color="primary">
                      Create account
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer with legal links */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          By signing in, you agree to our{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none'
              }
            }}
            onClick={() => setTermsModalOpen(true)}
          >
            Terms of Use
          </Typography>
          {' and '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none'
              }
            }}
            onClick={() => setPrivacyModalOpen(true)}
          >
            Privacy Policy
          </Typography>
        </Typography>
      </Box>

      {/* Developer footer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center'
        }}
      >
        <Typography variant="caption" color="text.disabled">
          Developed by Merit4all, all rights reserved.
        </Typography>
      </Box>

      {/* Legal Document Modals */}
      <LegalDocumentModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title="Terms of Use"
        htmlPath="/terms-of-use.html"
      />

      <LegalDocumentModal
        open={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        title="Privacy Policy"
        htmlPath="/privacy-policy.html"
      />
    </Box>
  );
};

export default Login;